"use client";

import { c as _c } from "react/compiler-runtime";
import { useLocaleContext, useTranslation } from "@/app/i18n/client";
import { BusCompanyMultiSelect } from "@/components/Home/HomeV2Content/buscompany-multiselect";
import { busrequestFormSchema, BusrequestFormSchema, calcCompanyTypeLabel } from "@/components/Home/HomeV2Content/busrequest-helper";
import SinglerequestCheckInputModalContent from "@/components/Home/HomeV2Content/SinglerequestCheckInputModalContent";
import Tooltip from "@/components/tooltip";
import { sendGTMEvent } from "@/components/Tracking/TagManagerWrapper";
import { AddressInputInner } from "@/components/ui/address-input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DateInput } from "@/components/ui/date-input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { InputForm } from "@/components/ui/input-form";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TimeInput } from "@/components/ui/time-input";
import { Modal } from "@/components/Util/Modal";
import { AnalyticsActions, AnalyticsCategories, AnalyticsEventType, AnalyticsLabels } from "@/constants/analytics";
import { AddRequest } from "@/http/LiveHttpService";
import { cn } from "@/lib/utils";
import { BusRequestCreateData } from "@/types/http";
import { getLocalizedRoute } from "@/utils/localized-route";
import { makeZodI18nMap } from "@daubit/zod-i18n-map";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { addDays } from "date-fns";
import { TFunction } from "i18next";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { Control, useForm, useWatch } from "react-hook-form";
import { Trans } from "react-i18next";
import { BsInfoCircle } from "react-icons/bs";
import { RiQuestionLine } from "react-icons/ri";
import z from "zod/v4";
export function BusrequestForm() {
  // react compiler deaktivieren
  // Müssen wir machen sonst wird <FormMessage> nicht korrekt aktualisiert
  "use no memo";

  const {
    t
  } = useTranslation(["common", "zod", "registerData", "zodCustomErrors", "zodBusrequestPaths"]);
  const form = useForm<z.infer<typeof busrequestFormSchema>>({
    resolver: zodResolver(busrequestFormSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      transferType: "transfertwoway",
      outwardDate: new Date(),
      returnDate: addDays(new Date(), 2),
      expanded: false,
      requestTarget: "collective",
      selectedCompanies: [],
      companyType: "PRIVATE",
      dsgvo: false
    }
  });
  z.config(makeZodI18nMap({
    t: t,
    ns: ["zod", "zodCustomErrors", "zodBusrequestPaths"]
  }));
  const transferType = useWatch({
    control: form.control,
    name: "transferType"
  });
  const isExpanded = useWatch({
    control: form.control,
    name: "expanded"
  });
  const requestTarget = useWatch({
    control: form.control,
    name: "requestTarget"
  });
  const companyType = useWatch({
    control: form.control,
    name: "companyType"
  });
  const outwardDate = useWatch({
    control: form.control,
    name: "outwardDate"
  });
  const returnDate = useWatch({
    control: form.control,
    name: "returnDate"
  });
  const lng = useLocaleContext();
  useEffect(() => {
    if (isExpanded && Object.values(form.formState.dirtyFields).filter(v => !!v).length > 0) form.trigger();
  }, [transferType]);
  useEffect(() => {
    if (form.getFieldState("returnDate").isDirty) form.trigger("returnDate");
  }, [outwardDate]);
  useEffect(() => {
    if (form.getFieldState("outwardDate").isDirty) {
      form.trigger("outwardDate");
    }
  }, [returnDate]);
  const [showCheckInputModal, setShowCheckInputModal] = useState<BusrequestFormSchema | null>(null);
  function onSubmit(data: BusrequestFormSchema) {
    console.log(data);
    setShowCheckInputModal(data);
  }
  const errorDialog = useRef<HTMLDialogElement>(null);
  const requestSubmittedDialog = useRef<HTMLDialogElement>(null);
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const createBusrequestMutation = useMutation({
    mutationFn: async (data_0: BusrequestFormSchema) => {
      const startTime = new Date().getTime();
      const busrequestToSend = createBusrequest(data_0 as BusrequestFormSchema);
      console.log("Sending Request:" + JSON.stringify(busrequestToSend));
      const backendRequest = await AddRequest(busrequestToSend);
      const endTime = new Date().getTime();
      const timeDifference = endTime - startTime;
      sendGTMEvent(AnalyticsEventType.Timing, AnalyticsCategories.singleRequestView, AnalyticsLabels.AddRequest, AnalyticsActions.sendData, {
        value: String(timeDifference)
      });
      console.log("Request: " + JSON.stringify(backendRequest));
      if (backendRequest.successful) {
        setIsSuccessOpen(true);
        if (requestSubmittedDialog?.current) {
          requestSubmittedDialog.current.showModal();
        }
        form.reset();
        sendGTMEvent(AnalyticsEventType.Event, AnalyticsCategories.singleRequestView, AnalyticsLabels.user, AnalyticsActions.formSubmitted);
        return true;
      }
      setIsErrorOpen(true);
      if (errorDialog?.current) {
        errorDialog.current.showModal();
      }
      sendGTMEvent(AnalyticsEventType.Error, AnalyticsCategories.singleRequestView, AnalyticsLabels.encounteredHTTPError, AnalyticsActions.submitFormClick, {
        fatal: "false"
      });
      throw new Error(backendRequest.error);
    }
  });
  function submitRequest(data_1: BusrequestFormSchema) {
    void createBusrequestMutation.mutateAsync(data_1);
  }
  return <Form {...form}>
      <Modal header={t("MAIN.HOMEV2.BUSREQUEST.CHECKDATA")} theme="dark" hideClose={true} open={showCheckInputModal !== null} disableOverflow>
        <SinglerequestCheckInputModalContent request={showCheckInputModal as BusrequestFormSchema | null} closeModal={(success?: boolean) => {
        if (success && showCheckInputModal) {
          submitRequest(showCheckInputModal);
        }
        setShowCheckInputModal(null);
      }} />
      </Modal>
      <dialog className="error" ref={errorDialog} onClose={() => setIsErrorOpen(false)}>
        {isErrorOpen && <div className="my-4">
            <h2>{t("MAIN.SINGLEREQUEST.SUBMIT_ERRORS.TITLE")}</h2>
            <p>{t("MAIN.SINGLEREQUEST.SUBMIT_ERRORS.TEXT")}</p>
          </div>}
        <form method="dialog">
          <button autoFocus={true}>{t("MAIN.OK.TEXT")}</button>
        </form>
      </dialog>
      <dialog ref={requestSubmittedDialog} onClose={() => setIsSuccessOpen(false)}>
        {isSuccessOpen && <div className="my-4">
            <h2>{t("MAIN.SINGLEREQUEST.CONFIRM_SUBMIT.TITLE")}</h2>
            <p>{t("MAIN.SINGLEREQUEST.CONFIRM_SUBMIT.TEXT")}</p>
          </div>}
        <form method="dialog">
          <Button variant="matchbus" size="lg" autoFocus={true}>
            {t("MAIN.OK.TEXT")}
          </Button>
        </form>
      </dialog>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <ul className="tab-list">
          <li className={transferType === "transferoneway" ? "tab-list-item tab-list-active" : "tab-list-item"}>
            <button onClick={() => {
            form.resetField("returnDate");
            form.setValue("transferType", "transferoneway");
          }} type="button">
              {t("MAIN.HOMEV2.BUSREQUEST.SIMPLE_TOUR")}
            </button>
          </li>
          <li className={transferType === "transfertwoway" ? "tab-list-item tab-list-active" : "tab-list-item"}>
            <button onClick={() => {
            form.setValue("returnDate", form.getValues("outwardDate") ?? new Date());
            form.setValue("transferType", "transfertwoway");
          }} type="button">
              {t("MAIN.HOMEV2.BUSREQUEST.TWO_WAY")}
            </button>
          </li>
          <li className={transferType === "transfertour" ? "tab-list-item tab-list-active" : "tab-list-item"}>
            <button onClick={() => {
            form.setValue("returnDate", form.getValues("outwardDate") ?? new Date());
            form.setValue("transferType", "transfertour");
          }} type="button">
              {t("MAIN.HOMEV2.BUSREQUEST.ROUND_TRIP")}
            </button>
          </li>
        </ul>
        <div className="relative flex flex-col gap-4">
          <Tooltip content={<div className="max-w-full">
                {t(`MAIN.HOMEV2.BUSREQUEST.EXPLANATION.${transferType}`)}
              </div>} className="absolute left-0 top-0 lg:top-4">
            <BsInfoCircle className="h-8 w-8 motion-safe:animate-pulse-less" />
          </Tooltip>
          <h3 className="mt-0 text-offWhite">
            {transferType !== "transfertour" ? t("MAIN.HOMEV2.BUSREQUEST.OUTBOUND") : t("MAIN.HOMEV2.BUSREQUEST.START")}
          </h3>
          <div className="grid grid-cols-1 items-start justify-start gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <FormField control={form.control} name="from" render={({
            field
          }) => <AddressInputInner label={t("MAIN.HOMEV2.BUSREQUEST.FROM")} type="region" {...field} />} />
            <FormField control={form.control} name="to" render={({
            field: field_0
          }) => <AddressInputInner label={t("MAIN.HOMEV2.BUSREQUEST.TO")} type="region" {...field_0} />} />
            <DateInput control={form.control} name="outwardDate" label={t("MAIN.HOMEV2.BUSREQUEST.FROM_DATE")} disabledMatcher={{
            before: new Date()
          }} />
            <TimeInput control={form.control} name="outwardDate" label={t("MAIN.HOMEV2.BUSREQUEST.DEP_TIME")} />
          </div>
          {(transferType === "transfertour" || transferType === "transfertwoway") && <>
              <hr className="w-full" />
              <h3 className="text-offWhite">
                {transferType === "transfertour" ? t("MAIN.HOMEV2.BUSREQUEST.FINISH") : t("MAIN.HOMEV2.BUSREQUEST.RETURN")}
              </h3>
              <div className="grid grid-cols-1 items-start justify-start gap-8 lg:grid-cols-4">
                <DateInput control={form.control} name="returnDate" label={t("MAIN.HOMEV2.BUSREQUEST.FROM_DATE")} disabledMatcher={{
              before: new Date()
            }} />
                <TimeInput control={form.control} name="returnDate" label={t("MAIN.HOMEV2.BUSREQUEST.DEP_TIME")} />
              </div>
            </>}
          <div className="text-end">
            <Button onClick={() => {
            form.setValue("expanded", true);
          }} variant="submitRed" size="xxl" className={cn(isExpanded && "hidden")}>
              {t("MAIN.HOMEV2.BUSREQUEST.GO_BUTTON")}
            </Button>
          </div>
          <div className={cn("flex flex-col gap-4", !isExpanded && "hidden")}>
            <hr className="w-full" />
            <div className="flex flex-col gap-4 lg:flex-row">
              <InputForm control={form.control} name="passengers" label={`${t("MANAGEMENT.ADD.PASSENGERS.TEXT")}*`} type="number" className="w-24" />
              <RequestTargetPicker control={form.control} />
              <BusCompanyMultiSelect control={form.control} name="selectedCompanies" visible={requestTarget === "individual"} />
            </div>
            <div>
              <FormField control={form.control} name="description" render={({
              field: field_1
            }) => <FormItem>
                    <FormLabel>{t("MAIN.HOMEV2.BUSREQUEST.DESCR")}</FormLabel>
                    <FormControl>
                      <Textarea {...field_1} className="h-auto rounded-none bg-white p-4 text-neutral-950 focus:border-matchbus md:text-lg" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xxl:grid-cols-4">
              <FormField control={form.control} name="companyType" render={({
              field: field_2
            }) => <FormItem>
                    <FormLabel>
                      {t("MAIN.HOMEV2.BUSREQUEST.USER_TYPE")}
                    </FormLabel>
                    <Select {...field_2} onValueChange={field_2.onChange}>
                      <FormControl>
                        <SelectTrigger className="h-auto rounded-none bg-white p-4 focus:border-matchbus md:text-lg">
                          <SelectValue placeholder={t("MAIN.HOMEV2.BUSREQUEST.USER_TYPE")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>
                            {t("MAIN.HOMEV2.BUSREQUEST.USER_TYPE")}
                          </SelectLabel>
                          <SelectItem value="SCHOOL">
                            {t("PROVIDER.COMPANYTYPE.SCHOOL.TEXT")}
                          </SelectItem>
                          <SelectItem value="CLUB">
                            {t("PROVIDER.COMPANYTYPE.CLUB.TEXT")}
                          </SelectItem>
                          <SelectItem value="COMPANY">
                            {t("PROVIDER.COMPANYTYPE.COMPANY.TEXT")}
                          </SelectItem>
                          <SelectItem value="AUTHORITY">
                            {t("PROVIDER.COMPANYTYPE.AUTHORITY.TEXT")}
                          </SelectItem>
                          <SelectItem value="PRIVATE">
                            {t("PROVIDER.COMPANYTYPE.PRIVATE.TEXT")}
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="firstName" render={({
              field: field_3
            }) => {
              const {
                value,
                ...fieldValues
              } = field_3;
              return <FormItem>
                      <FormLabel>
                        {t("MAIN.HOMEV2.BUSREQUEST.SURNAME")}*
                      </FormLabel>
                      <FormControl>
                        <Input type="text" {...fieldValues} value={value ?? ""} className="h-auto rounded-none bg-white p-4 text-neutral-950 focus:border-matchbus md:text-lg" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>;
            }} />
              <FormField control={form.control} name="lastName" render={({
              field: field_4
            }) => {
              const {
                value: value_0,
                ...fieldValues_0
              } = field_4;
              return <FormItem>
                      <FormLabel>{t("MAIN.HOMEV2.BUSREQUEST.NAME")}*</FormLabel>
                      <FormControl>
                        <Input type="text" {...fieldValues_0} value={value_0 ?? ""} className="h-auto rounded-none bg-white p-4 text-neutral-950 focus:border-matchbus md:text-lg" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>;
            }} />
              <span className="col-span-1">{/* Spacer */}</span>
              <FormField control={form.control} name="companyName" render={({
              field: field_5
            }) => {
              const {
                value: value_1,
                ...fieldValues_1
              } = field_5;
              return <FormItem className={cn("hidden", companyType !== "PRIVATE" && "block")}>
                      <FormLabel>
                        {calcCompanyTypeLabel(companyType, t)}*
                      </FormLabel>
                      <FormControl>
                        <Input type="text" {...fieldValues_1} value={value_1 ?? ""} className="h-auto rounded-none bg-white p-4 text-neutral-950 focus:border-matchbus md:text-lg" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>;
            }} />
              <FormField control={form.control} name="email" render={({
              field: field_6
            }) => {
              const {
                value: value_2,
                ...fieldValues_2
              } = field_6;
              return <FormItem>
                      <FormLabel>
                        {t("MAIN.HOMEV2.BUSREQUEST.EMAIL")}*
                      </FormLabel>
                      <FormControl>
                        <Input type="email" {...fieldValues_2} value={value_2 ?? ""} className="h-auto rounded-none bg-white p-4 text-neutral-950 focus:border-matchbus md:text-lg" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>;
            }} />
              <FormField control={form.control} name="phone" render={({
              field: field_7
            }) => {
              const {
                value: value_3,
                ...fieldValues_3
              } = field_7;
              return <FormItem>
                      <FormLabel>{t("MAIN.HOMEV2.BUSREQUEST.TEL")}</FormLabel>
                      <FormControl>
                        <Input type="tel" {...fieldValues_3} value={value_3 ?? ""} className="h-auto rounded-none bg-white p-4 text-neutral-950 focus:border-matchbus md:text-lg" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>;
            }} />
            </div>
            <div>
              <FormField control={form.control} name="dsgvo" render={({
              field: field_8
            }) => <FormItem>
                    <div className="flex flex-row items-center gap-4">
                      <FormControl>
                        <Checkbox name="dsgvo" ref={field_8.ref} disabled={field_8.disabled} onBlur={field_8.onBlur} checked={field_8.value} onCheckedChange={field_8.onChange} />
                      </FormControl>
                      <FormLabel className="text-xxs leading-normal sm:text-sm xl:text-normal">
                        <Trans t={t as TFunction} i18nKey={"MAIN.HOMEV2.BUSREQUEST.DATAPROTECTION.AGREEMENT"} components={[<Link key={getLocalizedRoute(lng, "privacypolicy")} href={getLocalizedRoute(lng, "privacypolicy")} target="_blank" className="text-xxs sm:text-sm xl:text-normal" />]} />
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>} />
            </div>
            <div className="text-end">
              <Button id="busrequest" variant="submitRed" size="xxl" disabled={createBusrequestMutation.isPending}>
                {t("MAIN.HOMEV2.BUSREQUEST.SEND_BUTTON")}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Form>;
}
type RequestTargetPickerProps = {
  control: Control<BusrequestFormSchema>;
};
function RequestTargetPicker(t0) {
  const $ = _c(5);
  const {
    control
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t2 => {
      const {
        field
      } = t2;
      return <FormItem><FormControl><RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="flex h-full flex-col justify-center gap-2"><div className="flex flex-row items-center gap-2"><RadioGroupItem value="collective" id="collective" /><Label htmlFor="collective">{t("MANAGEMENT.ADD.COLLECTIVE")}</Label><Tooltip content={t("MANAGEMENT.ADD.COLLECTIVETOOLTIP")}><div className="tooltip-icon"><RiQuestionLine size="1.5em" /></div></Tooltip></div><div className="flex flex-row items-center gap-2"><RadioGroupItem value="individual" id="individual" /><Label htmlFor="individual">{t("MANAGEMENT.ADD.INDIVIDUAL")}</Label><Tooltip content={t("MANAGEMENT.ADD.INDIVIDUALTOOLTIP")}><div className="tooltip-icon"><RiQuestionLine size="1.5em" /></div></Tooltip></div></RadioGroup></FormControl><FormMessage /></FormItem>;
    };
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== control || $[3] !== t1) {
    t2 = <FormField control={control} name="requestTarget" render={t1} />;
    $[2] = control;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function createBusrequest(values: z.infer<typeof busrequestFormSchema>): BusRequestCreateData {
  return {
    category: "grouprequest",
    address: values.address,
    companyName: values.companyType === "PRIVATE" ? `${values.firstName} ${values.lastName}` : values.companyName,
    companyType: values.companyType,
    description: values.description && values.description.trim() !== "" ? values.description : undefined,
    email: values.email,
    firstName: values.firstName,
    lastName: values.lastName,
    participants: values.passengers,
    start: {
      address: values.from,
      date: values.outwardDate!.valueOf()
    },
    end: {
      address: values.to,
      date: values.transferType === "transferoneway" ? 0 : values.returnDate!.valueOf()
    },
    telephone: values.phone,
    transferType: values.transferType,
    fromSingleRequest: true,
    state: "pending",
    activityRadius: 100,
    target: values.requestTarget,
    targetedCompanies: values.requestTarget === "individual" ? values.selectedCompanies : undefined
  };
}